import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FormContainer from "../shared/FormContainer";
import CustomInput from "../shared/CustomInput";
import Button from "../shared/Button";
import { AlertCircleIcon } from "../../assets/icons";
import { Api } from "../../network/api";

const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [tokenLoading, setTokenLoading] = useState(true);
  const [token, setToken] = useState("");

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    console.log("tokenFromUrl", tokenFromUrl);
    if (!tokenFromUrl) {
      setErrors({
        general: "Invalid reset link. Please request a new password reset link.",
      });
      setTokenLoading(false);
      return;
    }
    setToken(tokenFromUrl);
    setTokenLoading(false);
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await Api.resetPassword(token, formData.password);
      if (response?.data?.success || response?.status === 200) {
        setMessage(response?.data?.message);
        setSuccess(true);
      } else {
        setErrors({ general: "Failed to reset password. Please try again." });
      }
    } catch (error) {
      console.error("Reset password error:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to reset password. Please try again.";
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const handleRequestNewReset = () => {
    navigate("/forgot-password");
  };

  if (tokenLoading) {
    return (
      <FormContainer
        title="Validating Reset Link"
        subtitle="Please wait while we verify your reset link"
      >
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </FormContainer>
    );
  }

  if (!token) {
    return (
      <FormContainer
        title="Invalid Reset Link"
        subtitle="The reset link is invalid or has expired"
      >
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-left animate-fadeInUp">
            <div className="flex items-center">
              <AlertCircleIcon className="w-10 h-10 mr-2" stroke="#b91c1c" />
              {errors.general}
            </div>
          </div>

          <div className="text-center space-y-4 animate-fadeInUp animation-delay-100">
            <p className="text-sm text-gray-600">
              The password reset link you're trying to use is invalid or has
              expired.
            </p>
            <p className="text-xs text-gray-500">
              Password reset links expire after 24 hours for security reasons.
            </p>
          </div>

          <div className="space-y-3 animate-fadeInUp animation-delay-200">
            <Button
              type="button"
              onClick={handleRequestNewReset}
              className="w-full hover-lift"
              size="large"
            >
              Request New Reset Link
            </Button>

            <Button
              type="button"
              onClick={handleBackToLogin}
              variant="secondary"
              className="w-full hover-lift"
              size="large"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </FormContainer>
    );
  }

  if (success) {
    return (
      <FormContainer
        title="Password Reset Successfully"
        subtitle="Your password has been updated"
      >
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-left animate-fadeInUp">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {message}
            </div>
          </div>

          <div className="text-center space-y-4 animate-fadeInUp animation-delay-100">
            <p className="text-sm text-gray-600">
              You can now log in with your new password.
            </p>
            <p className="text-xs text-gray-500">
              For security reasons, all other sessions have been logged out.
            </p>
          </div>

          <div className="animate-fadeInUp animation-delay-200">
            <Button
              type="button"
              onClick={handleBackToLogin}
              className="w-full hover-lift"
              size="large"
            >
              Continue to Login
            </Button>
          </div>
        </div>
      </FormContainer>
    );
  }

  return (
    <FormContainer title="Reset Password" subtitle="Enter your new password">
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-left animate-shake">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {errors.general}
            </div>
          </div>
        )}

        <div className="animate-fadeInUp animation-delay-100">
          <CustomInput
            type="password"
            name="password"
            label="New Password"
            placeholder="Enter your new password"
            value={formData.password}
            onChange={handleInputChange}
            error={errors.password}
            required
            autoComplete="new-password"
          />
        </div>

        <div className="animate-fadeInUp animation-delay-200">
          <CustomInput
            type="password"
            name="confirmPassword"
            label="Confirm New Password"
            placeholder="Confirm your new password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            error={errors.confirmPassword}
            required
            autoComplete="new-password"
          />
        </div>

        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl text-left animate-fadeInUp animation-delay-300">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="text-sm">
              <p className="font-medium mb-1">Password Requirements:</p>
              <ul className="text-xs space-y-1">
                <li>• At least 8 characters long</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="animate-fadeInUp animation-delay-400">
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
            className="w-full hover-lift"
            size="large"
          >
            Reset Password
          </Button>
        </div>

        <div className="text-center animate-fadeInUp animation-delay-500">
          <p className="text-sm text-gray-600">
            Remember your password?{" "}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200 hover:underline"
            >
              Back to login
            </button>
          </p>
        </div>
      </form>
    </FormContainer>
  );
};

export default ResetPasswordForm;
